import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';

import { roomAtom } from '../../core/atoms';
import type { ContainerComponentProps } from '../../core/types';
import { observeShadowRoot } from './observeShadowRoot';

/**
 * Zoom recommends using a single <video-player-container> parent element,
 * for all user video components (<video-player>).
 * - Screen share based <video-player> should get their own <video-player-container>.
 *
 * This is supposed to optimize performance:
 * https://devforum.zoom.us/t/should-video-player-be-a-direct-child-of-video-player-container-help-needed/125828/4
 */
export function ZoomVideoContainer({ className, dataTestId, styleOverride, children }: ContainerComponentProps) {
  const room = useAtomValue(roomAtom);
  const containerRef = useRef<HTMLElement | null>(null);

  useEffect(
    function observeVideoContainerShadowRoot() {
      if (!room) return;

      // set up observer when shadowRoot is most likely ready
      const observer = observeShadowRoot(containerRef.current);
      if (!observer) {
        return;
      }

      return () => {
        observer.disconnect();
      };
    },
    [room],
  );

  return (
    <video-player-container ref={containerRef} class={className} style={styleOverride} data-testid={dataTestId}>
      {children}
    </video-player-container>
  );
}
